import React, { useEffect, useState } from "react";
import {
    Autocomplete,
    Button,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import type { AnswerSet, AnswerValue } from "../../../../../../types/answerSet";
import { fetchAnswerSet, fetchQuestion } from "../../../../../../utils/api";
import { toNum } from "../OperandBuilder/constants";
import { OperandBuilder } from "../OperandBuilder";
import type { RuleComponentProps } from "./types";

function getAnswerLabel(a: AnswerValue): string {
    return a.displayText ?? a.name ?? a.tagLabel ?? (a.id != null ? String(a.id) : "");
}

export const RuleIn: React.FC<RuleComponentProps> = ({
    value,
    onChange,
    depth,
    evaluationDomain,
    declaredDerivedAttributeNames = [],
}) => {
    const left = value?.in?.left ?? { value: "" };
    const right = value?.in?.right ?? [];

    const [answerSet, setAnswerSet] = useState<AnswerSet | null>(null);
    const [answerSetLoading, setAnswerSetLoading] = useState(false);
    const isLeftAnswer = left?.operandType === "answer";
    const qId = toNum(left?.questionId);
    const asId = toNum(left?.answerSetId);

    useEffect(() => {
        if (!isLeftAnswer || (asId == null && qId == null)) {
            setAnswerSet(null);
            return;
        }
        let cancelled = false;
        setAnswerSetLoading(true);
        const load = asId != null
            ? fetchAnswerSet(asId)
            : fetchQuestion(qId!).then((q) => {
                if (q.answers?.length)
                    return { id: 0, name: q.answerSetName ?? "", answers: q.answers } as AnswerSet;
                const id = q.answerSetId != null ? Number(q.answerSetId) : null;
                return id != null ? fetchAnswerSet(id) : Promise.resolve(null);
            });
        load
            .then((as) => {
                if (!cancelled && as) setAnswerSet(as);
                else if (!cancelled) setAnswerSet(null);
            })
            .catch(() => {
                if (!cancelled) setAnswerSet(null);
            })
            .finally(() => {
                if (!cancelled) setAnswerSetLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isLeftAnswer, asId, qId]);

    const updateLeft = (op: import("../../../../../../types/resolver").OperandDto | null) => {
        onChange({ in: { left: op ?? { value: "" }, right } });
    };

    const updateRight = (next: unknown[]) => {
        onChange({ in: { left, right: next } });
    };

    const addItem = () => updateRight([...right, ""]);
    const updateItem = (index: number, item: unknown) => {
        const next = right.slice();
        next[index] = item;
        updateRight(next);
    };
    const removeItem = (index: number) => {
        const next = right.slice();
        next.splice(index, 1);
        updateRight(next);
    };

    const answerOptions = answerSet?.answers ?? [];
    const selectedAnswerIds = Array.isArray(right)
        ? right.map((v) => (typeof v === "number" ? String(v) : String(v)))
        : [];
    const selectedAnswers = answerOptions.filter(
        (a) => a.id != null && selectedAnswerIds.includes(String(a.id))
    );
    const handleAnswerMultiChange = (_: unknown, newValue: AnswerValue[]) => {
        updateRight(newValue.map((a) => (a.id != null ? String(a.id) : "")).filter(Boolean));
    };

    return (
        <Stack spacing={1}>
            <OperandBuilder
                label="Left"
                side="left"
                value={left}
                onChange={updateLeft}
                evaluationDomain={evaluationDomain}
                declaredDerivedAttributeNames={declaredDerivedAttributeNames}
            />
            <Typography variant="caption">Values in list</Typography>
            {isLeftAnswer && (asId != null || qId != null) ? (
                <Autocomplete<AnswerValue, true>
                    multiple
                    size="small"
                    loading={answerSetLoading}
                    options={answerOptions}
                    value={selectedAnswers}
                    getOptionLabel={getAnswerLabel}
                    isOptionEqualToValue={(a, b) => (a.id ?? a.name) === (b.id ?? b.name)}
                    onChange={handleAnswerMultiChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Answers"
                            placeholder={answerSetLoading ? "Loading…" : "Select answers"}
                        />
                    )}
                />
            ) : (
                <>
                    {right.map((item, i) => (
                        <Stack key={`in-item-${depth}-${i}`} direction="row" spacing={1} alignItems="center">
                            <TextField
                                size="small"
                                value={typeof item === "string" ? item : String(item)}
                                onChange={(e) => updateItem(i, e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <IconButton size="small" onClick={() => removeItem(i)} color="error">
                                <Delete fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button size="small" variant="outlined" onClick={addItem}>
                        Add value
                    </Button>
                </>
            )}
        </Stack>
    );
};
